import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  addDoc,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const ServiceRow = ({ service, expenses, t }) => {
  const [open, setOpen] = useState(false);
  const total = expenses.reduce((sum, expense) => sum + expense.amount, 0);
  const logo = serviceLogos[service];

  return (
    <>
      <TableRow>
        <TableCell>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            {logo && (
              <Box
                component="img"
                src={logo.image}
                alt={logo.alt}
                sx={{
                  width: 20,
                }}
              />
            )}
            {service}
          </Box>
        </TableCell>
        <TableCell align="right">
          {new Intl.NumberFormat('fr-CA', {
            style: 'currency',
            currency: 'CAD',
          }).format(total)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={2}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('dashboard.expenses.date')}</TableCell>
                    <TableCell align="right">
                      {t('dashboard.expenses.amount')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenses.map((expense) => (
                    <TableRow key={expense.id}>
                      <TableCell>{formatDate(expense.date)}</TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat('fr-CA', {
                          style: 'currency',
                          currency: 'CAD',
                        }).format(expense.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const formatDate = (date) => {
  try {
    if (!date) return '';
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    return format(dateObj, 'MMM d, yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

const serviceLogos = {
  GCP: {
    name: 'Google Cloud Platform',
    image:
      'https://static-00.iconduck.com/assets.00/google-cloud-icon-2048x1646-7admxejz.png',
    alt: 'GCP Logo',
  },
  FF: {
    name: 'Flutterflow',
    image:
      'https://docs.flutterflow.io/logos/logoMark_outlinePrimary_transparent.svg',
    alt: 'FF Logo',
  },
  OPENAI: {
    name: 'OpenAI',
    image:
      'https://static-00.iconduck.com/assets.00/openai-icon-2021x2048-4rpe5x7n.png',
    alt: 'OpenAI Logo',
  },
  READAI: {
    name: 'ReadAI',
    image: 'https://www.fahimai.com/wp-content/uploads/2024/08/CTA-1-5.png',
    alt: 'ReadAI Logo',
  },
  BYTESCALE: {
    name: 'Bytescale',
    image: 'https://blog.bytescale.com/content/images/2023/08/Paddle.png',
    alt: 'Bytescale Logo',
  },
  SQUARESPACE: {
    name: 'Squarespace',
    image:
      'https://cdn.iconscout.com/icon/free/png-256/free-squarespace-logo-icon-download-in-svg-png-gif-file-formats--company-brand-world-logos-vol-8-pack-icons-282893.png?f=webp',
    alt: 'Squarespace Logo',
  },
};

const ExpensesList = ({ startDate, endDate }) => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [newExpense, setNewExpense] = useState({
    service: '',
    amount: '',
    date: new Date(),
  });
  const { t } = useTranslation();

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setNewExpense({
      service: '',
      amount: '',
      date: new Date(),
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewExpense((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (date) => {
    setNewExpense((prev) => ({
      ...prev,
      date,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const expenseData = {
        ...newExpense,
        amount: parseFloat(newExpense.amount),
        date: newExpense.date,
      };

      const docRef = await addDoc(collection(db, 'expenses'), expenseData);
      setExpenses((prev) => [...prev, { id: docRef.id, ...expenseData }]);
      handleCloseModal();
    } catch (err) {
      console.error('Error adding expense:', err);
      setError(t('dashboard.expenses.addError'));
    }
  };

  useEffect(() => {
    const fetchExpenses = async () => {
      if (!startDate || !endDate) return;

      setLoading(true);
      setError(null);

      try {
        const expensesRef = collection(db, 'expenses');
        const q = query(
          expensesRef,
          where('date', '>=', startDate),
          where('date', '<=', endDate),
          orderBy('date', 'desc')
        );

        const querySnapshot = await getDocs(q);
        const expensesData = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
          };
        });

        setExpenses(expensesData);
      } catch (err) {
        console.error('Error fetching expenses:', err);
        setError(t('dashboard.expenses.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchExpenses();
  }, [startDate, endDate, t]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Grouper les dépenses par service
  const groupedExpenses = expenses.reduce((acc, expense) => {
    if (!acc[expense.service]) {
      acc[expense.service] = [];
    }
    acc[expense.service].push(expense);
    return acc;
  }, {});

  const totalAmount = expenses.reduce(
    (sum, expense) => sum + expense.amount,
    0
  );

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">{t('dashboard.expenses.title')}</Typography>
        <IconButton color="primary" onClick={handleOpenModal} size="small">
          <AddIcon />
        </IconButton>
      </Box>

      <TableContainer elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('dashboard.expenses.service')}</TableCell>
              <TableCell align="right">
                {t('dashboard.expenses.amount')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(groupedExpenses).map(
              ([service, serviceExpenses]) => (
                <ServiceRow
                  key={service}
                  service={service}
                  expenses={serviceExpenses}
                  t={t}
                />
              )
            )}
            <TableRow>
              <TableCell>
                <strong>{t('dashboard.expenses.total')}</strong>
              </TableCell>
              <TableCell align="right">
                <strong>
                  {new Intl.NumberFormat('fr-CA', {
                    style: 'currency',
                    currency: 'CAD',
                  }).format(totalAmount)}
                </strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>{t('dashboard.expenses.addExpense')}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="service"
              label={t('dashboard.expenses.service')}
              type="text"
              fullWidth
              value={newExpense.service}
              onChange={handleInputChange}
              required
            />
            <TextField
              margin="dense"
              name="amount"
              label={t('dashboard.expenses.amount')}
              type="number"
              fullWidth
              value={newExpense.amount}
              onChange={handleInputChange}
              required
              inputProps={{ step: '0.01' }}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t('dashboard.expenses.date')}
                value={newExpense.date}
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField {...params} margin="dense" fullWidth required />
                )}
              />
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained" color="primary">
              {t('common.save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default ExpensesList;
